<script setup lang="ts">
import {capitalize} from "vue";
import {useCustomTariffStore} from "~/stores/useCustomTariffStore";
import IconPriceTraffic from "~/components/sx/icons/prices/IconPriceTraffic.vue";
import IconPriceUnlim from "~/components/sx/icons/prices/IconPriceUnlim.vue";
import {UiIcon} from "@common/ui-kit";
import type{IUnlimTariff} from "~/components/sx/prices/PricesUnlimItem.vue";

const { t, locale: localeI18n } = useI18n();
const { tariffs } = useCustomTariffStore();

const items = computed(() => {
  return {
    traffic: {
      type: 'traffic',
      titleKey: 'components.popupPrices.traffic.title',
      descriptionKey: 'components.popupPrices.traffic.description',
      tariff: tariffs.value.promise[1] ?? null,
      icon: markRaw(IconPriceTraffic),
    },
    unlim: {
      type: 'unlim',
      titleKey: 'components.popupPrices.unlim.title',
      descriptionKey: 'components.popupPrices.unlim.description',
      tariff: tariffs.value.unlim[1] as IUnlimTariff ?? null,
      icon: markRaw(IconPriceUnlim),
    },
  }
})

function formatSmart(num: number) {
  const fixed = num.toFixed(2);
  return fixed.endsWith('0') ? num.toFixed(1) : fixed;
}
</script>

<template>
  <div class="popup-prices">
    <div class="popup-prices__title">
      {{ capitalize($t('components.popupPrices.title')) }}
    </div>
    <div class="popup-prices__inner">
      <div
        v-for="(item, index) in items"
        :key="index"
        class="popup-prices__item"
      >
        <div class="popup-prices__subtitle">
          <component :is="item.icon" class="popup-prices__icon" />
          {{ capitalize($t(item.titleKey)) }}
        </div>
        <p class="popup-prices__description">
          {{ capitalize($t(item.descriptionKey)) }}
        </p>
        <NuxtLink :to="`/${localeI18n}/#prices`" class="popup-prices__btn">
          {{ $t('components.popupPrices.pricesBtn') }}
          <UiIcon
            name="new-windows"
            type="400"
            size="sm"
          />
        </NuxtLink>
        <div class="popup-prices__divider" />
        <div class="popup-prices__tariff">
          <div class="popup-prices__tag">
            {{ capitalize($t('components.popupPrices.mostProfitable')) }}
          </div>
          <div v-if="item.type === 'traffic'" class="popup-prices__price">
            ${{ item.tariff?.price }}
            <span class="popup-prices__sub">
              / {{ $t('pages.home.prices.common.month') }}
            </span>
          </div>

          <div v-else-if="item.tariff" class="popup-prices__price">
            ${{ item.tariff.streams[0]?.price }}
            <span class="popup-prices__sub">
              / {{ $t('pages.home.prices.common.month') }}
            </span>
          </div>

          <div class="popup-prices__divider base" />

          <div v-if="item.type === 'traffic' && item.tariff?.types" class="popup-prices__info">
            <div
              v-for="(type, index) in item.tariff.types"
              :key="index"
              class="popup-prices__advantage"
            >
              <div class="popup-prices__key">
                {{ $t(type.name) }}
              </div>
              <div class="popup-prices__value">
                ${{ type.price }} / GB
              </div>
            </div>
          </div>

          <div v-else-if="item.tariff" class="popup-prices__info">
            <div class="popup-prices__advantage">
              <div class="popup-prices__key">
                {{ capitalize($t('pages.home.prices.unlimItem.pricePerProxy')) }}
              </div>
              <div class="popup-prices__value">
                ${{ formatSmart(item.tariff.streams[0]?.price / item.tariff.proxies_count) }} {{ $t('pages.home.prices.common.perMonth') }}
              </div>
            </div>
            <div class="popup-prices__advantage">
              <div class="popup-prices__key">
                {{ capitalize($t('pages.home.prices.unlimItem.proxyCountFull')) }}
              </div>
              <div class="popup-prices__value">
                {{ item.tariff.proxies_count }}
              </div>
            </div>
            <div class="popup-prices__advantage">
              <div class="popup-prices__key">
                {{ $t('pages.home.prices.unlimItem.limitation') }}
              </div>
              <div v-if="false" class="popup-prices__value">
                {{ item.tariff.streams[0]?.quantity }} {{ $t('pages.home.prices.unlimItem.streams') }}
              </div>
              <div class="popup-prices__value green">
                {{ $t('pages.home.prices.unlimItem.unlimited') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.popup-prices {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  &__title {
    padding-bottom: 12px;
    border-bottom: var(--border-sx-main);
    font-weight: 500;
    font-size: 22px;
    line-height: 20px;
    letter-spacing: 0;
  }
  &__inner {
    display: flex;
    gap: 32px;
    @media (max-width: 1199px) {
      flex-wrap: wrap;
    }
  }
  &__item {
    width: 320px;
    height: auto;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-self: stretch;
  }
  &__content {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  &__subtitle {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  &__icon {
    width: 20px;
    height: 20px;
  }
  &__description {
    font-size: 14px;
    line-height: 16px;
    color: #666666;
    margin-block: 6px 12px;
  }
  &__btn {
    display: flex;
    width: max-content;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    padding: 10px 16px;
    height: 36px;
    border-radius: 40px;
    white-space: nowrap;
    color: #000;
    border: 1px solid #000;
    &:hover {
      background: #eeeeee;
    }
  }
  &__divider {
    border-bottom: var(--border-sx-main);
    width: 100%;
    margin-block: 16px;
    &.base {
      margin: 0;
    }
  }
  &__tariff {
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: var(--bg-sx-gray);
    border: var(--border-sx-main);
    padding: 16px;
    border-radius: 16px;
  }
  &__tag {
    display: flex;
    width: max-content;
    align-items: center;
    justify-content: center;
    padding: 2px 4px;
    background: var(--bg-sx-semigray);
    border-radius: 8px;
  }
  &__price {
    font-weight: 700;
    font-size: 24px;
    line-height: 100%;
    letter-spacing: -3%;
  }
  &__sub {
    font-weight: 400;
    font-size: 14px;
    line-height: 100%;
    text-transform: uppercase;
    color: var(--font-sx-color-gray);
  }
  &__info {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  &__advantage {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 12px;
    line-height: 24px;
  }
  &__key {
    color: #666666;
  }
  &__value {
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    &.green {
      color: var(--font-sx-color-green);
    }
  }
}
</style>